/* Goals — Sofia's mål och delmål. Cards with progress bars + milestone check-offs
 * + push-to-flow directly from milestone. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Plus, Trash2, Edit2, Check, X, Target, Send, Calendar as Cal,
  ChevronDown, ChevronRight, Sparkles, Archive, Star,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingGoal, setAddingGoal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        axios.get(`${API}/goals?archived=${showArchived}`, { withCredentials: true }),
        axios.get(`${API}/goals/summary/overview`, { withCredentials: true }),
      ]);
      setGoals(list.goals || []);
      setCategories(list.categories || []);
      setSummary(s);
    } catch { toast.error('Kunde inte ladda mål'); }
    finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Laddar mål…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="goals-page">
      {/* Hero */}
      <div className="rounded-3xl p-5 border relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #FFF0F5 0%, #F0F4FF 100%)', borderColor: '#F3D0D9' }}>
        <div className="absolute -right-4 -top-4 opacity-15 text-7xl leading-none pointer-events-none">🎯</div>
        <div className="text-[11px] uppercase font-semibold tracking-widest text-rose-800/70">Mina mål</div>
        <h1 className="text-2xl font-bold text-rose-950" style={{ fontFamily: '"Caveat", ui-serif, Georgia, serif' }}>
          Mål och delmål 💗
        </h1>
        <p className="text-sm text-rose-900/80 mt-1">
          Stora drömmar. Små, konkreta steg. En sak i taget 🌸
        </p>
        {summary && (
          <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
            <span className="rounded-full px-2 py-0.5 bg-white/70 border border-rose-200 text-rose-800">
              {summary.active_goals} aktiva mål
            </span>
            <span className="rounded-full px-2 py-0.5 bg-white/70 border border-emerald-200 text-emerald-800">
              {summary.milestones_done} / {summary.milestones_total} delmål klara
            </span>
            <span className="rounded-full px-2 py-0.5 bg-white/70 border border-amber-200 text-amber-800">
              {summary.milestones_done_30d} klara senaste 30d
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => setAddingGoal(true)}
          data-testid="goals-add-btn"
          className="text-xs px-4 py-2 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white font-semibold shadow inline-flex items-center gap-1.5 hover:opacity-90">
          <Plus className="w-3.5 h-3.5" /> Nytt mål
        </button>
        <button
          onClick={() => setShowArchived((v) => !v)}
          data-testid="goals-toggle-archived"
          className="text-[11px] px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-600 inline-flex items-center gap-1 hover:bg-slate-50">
          <Archive className="w-3 h-3" /> {showArchived ? 'Aktiva' : 'Arkiv'}
        </button>
      </div>

      {addingGoal && (
        <GoalForm
          categories={categories}
          onCancel={() => setAddingGoal(false)}
          onSaved={async () => { setAddingGoal(false); await load(); toast.success('Mål skapat 💜'); }}
        />
      )}

      {goals.length === 0 && !addingGoal && (
        <div className="rounded-2xl bg-white border border-rose-100 p-8 text-center">
          <Target className="w-8 h-8 text-rose-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">
            {showArchived ? 'Inga arkiverade mål än' : 'Inga mål än — vad drömmer du om?'}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {goals.map((g) => (
          <GoalCard key={g.id} goal={g} categories={categories} onChanged={load} />
        ))}
      </div>
    </div>
  );
}

function GoalCard({ goal, categories, onChanged }) {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [addingMs, setAddingMs] = useState(false);
  const [newMs, setNewMs] = useState('');
  const cat = categories.find((c) => c.id === goal.category) || { emoji: '✨', label: 'Annat', color: '#F5F5F5' };

  const toggleMs = async (msId, done) => {
    try {
      await axios.patch(`${API}/goals/${goal.id}/milestones/${msId}`, { done }, { withCredentials: true });
      await onChanged();
    } catch { toast.error('Kunde inte spara'); }
  };

  const delMs = async (msId) => {
    if (!window.confirm('Ta bort delmålet?')) return;
    try {
      await axios.delete(`${API}/goals/${goal.id}/milestones/${msId}`, { withCredentials: true });
      await onChanged();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const pushMs = async (msId) => {
    try {
      await axios.post(`${API}/goals/${goal.id}/milestones/${msId}/push-to-flow`, {}, { withCredentials: true });
      toast.success('Skickat till Idag-lista 💜');
    } catch { toast.error('Kunde inte skicka'); }
  };

  const addMs = async () => {
    if (newMs.trim().length < 2) { toast.error('Skriv minst 2 tecken'); return; }
    try {
      await axios.post(`${API}/goals/${goal.id}/milestones`, { title: newMs }, { withCredentials: true });
      setNewMs(''); setAddingMs(false);
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte spara'); }
  };

  const delGoal = async () => {
    if (!window.confirm(`Ta bort målet "${goal.title}" och alla delmål?`)) return;
    try {
      await axios.delete(`${API}/goals/${goal.id}`, { withCredentials: true });
      await onChanged();
      toast.success('Målet borttaget');
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const toggleArchive = async () => {
    try {
      await axios.patch(`${API}/goals/${goal.id}`, { archived: !goal.archived }, { withCredentials: true });
      await onChanged();
      toast.success(goal.archived ? 'Återställt' : 'Arkiverat');
    } catch { toast.error('Kunde inte spara'); }
  };

  return (
    <div className="rounded-2xl border p-4 bg-white shadow-sm"
      style={{ borderColor: cat.color, borderLeftWidth: 4 }}
      data-testid={`goal-card-${goal.id}`}>
      {editing ? (
        <GoalForm
          initial={goal}
          categories={categories}
          onCancel={() => setEditing(false)}
          onSaved={async () => { setEditing(false); await onChanged(); }}
        />
      ) : (
        <>
          <div className="flex items-start gap-3">
            <div className="text-3xl leading-none">{cat.emoji}</div>
            <div className="flex-1 min-w-0">
              <button onClick={() => setExpanded((e) => !e)} className="text-left w-full">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[11px] uppercase font-semibold tracking-wider text-slate-500">{cat.label}</div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-1">
                      {goal.title}
                      {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                    </h3>
                  </div>
                  {goal.days_left !== null && goal.days_left !== undefined && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${
                      goal.days_left < 0 ? 'bg-red-100 text-red-800' :
                      goal.days_left < 7 ? 'bg-amber-100 text-amber-800' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      <Cal className="w-2.5 h-2.5 inline mr-0.5" />
                      {goal.days_left < 0 ? `${Math.abs(goal.days_left)}d sedan` : `${goal.days_left}d kvar`}
                    </span>
                  )}
                </div>
              </button>

              {/* Progress bar */}
              <div className="mt-2">
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="text-slate-600">{goal.progress.done} / {goal.progress.total} delmål</span>
                  <span className="font-semibold text-slate-800">{goal.progress.percent}%</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-rose-400 to-fuchsia-500 transition-all"
                    style={{ width: `${goal.progress.percent}%` }} />
                </div>
              </div>

              {goal.why && expanded && (
                <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900 italic">
                  💡 <b>Varför:</b> {goal.why}
                </div>
              )}

              {goal.description && expanded && (
                <p className="text-xs text-slate-600 mt-2">{goal.description}</p>
              )}
            </div>
          </div>

          {expanded && (
            <div className="mt-3 pl-11 space-y-1.5" data-testid={`goal-milestones-${goal.id}`}>
              {goal.milestones.map((m) => (
                <MilestoneRow key={m.id} milestone={m} goalId={goal.id}
                  onToggle={(d) => toggleMs(m.id, d)}
                  onDelete={() => delMs(m.id)}
                  onPush={() => pushMs(m.id)}
                  onChanged={onChanged}
                />
              ))}
              {addingMs ? (
                <div className="flex gap-1.5 items-center">
                  <input
                    value={newMs} onChange={(e) => setNewMs(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addMs()}
                    placeholder="Nytt delmål…"
                    data-testid={`goal-ms-input-${goal.id}`}
                    className="flex-1 text-sm rounded-lg border border-rose-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-200"
                    autoFocus />
                  <button onClick={addMs} data-testid={`goal-ms-save-${goal.id}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white">Spara</button>
                  <button onClick={() => { setAddingMs(false); setNewMs(''); }}
                    className="text-slate-400"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <button onClick={() => setAddingMs(true)}
                  data-testid={`goal-add-ms-${goal.id}`}
                  className="w-full text-xs px-3 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 border border-dashed border-rose-300 text-rose-800 inline-flex items-center justify-center gap-1">
                  <Plus className="w-3 h-3" /> Lägg till delmål
                </button>
              )}

              <div className="pt-2 flex gap-2 justify-end">
                <button onClick={() => setEditing(true)}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600 inline-flex items-center gap-1">
                  <Edit2 className="w-2.5 h-2.5" /> Redigera
                </button>
                <button onClick={toggleArchive}
                  data-testid={`goal-archive-${goal.id}`}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600 inline-flex items-center gap-1">
                  <Archive className="w-2.5 h-2.5" /> {goal.archived ? 'Återställ' : 'Arkivera'}
                </button>
                <button onClick={delGoal}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-red-200 text-red-600 inline-flex items-center gap-1">
                  <Trash2 className="w-2.5 h-2.5" /> Ta bort
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MilestoneRow({ milestone, goalId, onToggle, onDelete, onPush, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(milestone.title);

  const save = async () => {
    if (text.trim().length < 2) { toast.error('För kort'); return; }
    try {
      await axios.patch(`${API}/goals/${goalId}/milestones/${milestone.id}`, { title: text }, { withCredentials: true });
      setEditing(false);
      await onChanged();
    } catch { toast.error('Kunde inte spara'); }
  };

  if (editing) {
    return (
      <div className="flex gap-1.5 items-center">
        <input value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && save()}
          className="flex-1 text-sm rounded-lg border border-rose-200 px-2 py-1"
          autoFocus />
        <button onClick={save} className="text-emerald-600"><Check className="w-3.5 h-3.5" /></button>
        <button onClick={() => { setEditing(false); setText(milestone.title); }} className="text-slate-400"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 p-1.5 rounded-lg group ${milestone.done ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
      data-testid={`milestone-${milestone.id}`}>
      <button onClick={() => onToggle(!milestone.done)}
        data-testid={`milestone-toggle-${milestone.id}`}
        className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
          milestone.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-rose-300 bg-white hover:border-rose-500'
        }`}>
        {milestone.done && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
      </button>
      <div className={`flex-1 text-sm ${milestone.done ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
        {milestone.title}
      </div>
      <div className="opacity-0 group-hover:opacity-100 flex gap-1">
        <button onClick={onPush} title="Skicka till Idag-lista"
          data-testid={`milestone-push-${milestone.id}`}
          className="text-slate-400 hover:text-rose-600">
          <Send className="w-3 h-3" />
        </button>
        <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-slate-700">
          <Edit2 className="w-3 h-3" />
        </button>
        <button onClick={onDelete} className="text-slate-400 hover:text-red-500">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function GoalForm({ initial, categories, onCancel, onSaved }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [why, setWhy] = useState(initial?.why || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [category, setCategory] = useState(initial?.category || 'annat');
  const [targetDate, setTargetDate] = useState(initial?.target_date || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (title.trim().length < 2) { toast.error('Skriv en titel'); return; }
    setSaving(true);
    try {
      const body = { title, why, description, category, target_date: targetDate || null };
      if (initial) {
        await axios.patch(`${API}/goals/${initial.id}`, body, { withCredentials: true });
        toast.success('Sparat 💜');
      } else {
        await axios.post(`${API}/goals`, body, { withCredentials: true });
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte spara'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 space-y-2" data-testid="goal-form">
      <input value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder="T.ex. Springa 5 km utan pauser"
        data-testid="goal-form-title"
        className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200"
        autoFocus />
      <textarea value={why} onChange={(e) => setWhy(e.target.value)}
        placeholder="Varför är detta viktigt för dig? (motivation när det är tungt)"
        rows={2}
        data-testid="goal-form-why"
        className="w-full text-xs rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200" />
      <textarea value={description} onChange={(e) => setDescription(e.target.value)}
        placeholder="Beskrivning (valfritt)"
        rows={2}
        className="w-full text-xs rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200" />
      <div>
        <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-1">Kategori</div>
        <div className="flex flex-wrap gap-1">
          {categories.map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)}
              data-testid={`goal-form-cat-${c.id}`}
              className={`text-[11px] px-2 py-0.5 rounded-full border ${
                category === c.id ? 'bg-rose-500 text-white border-rose-500' : 'bg-white border-slate-200 text-slate-600'
              }`}>
              {c.emoji} {c.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-1">Måldatum (valfritt)</div>
        <input type="date" value={targetDate || ''} onChange={(e) => setTargetDate(e.target.value)}
          data-testid="goal-form-target"
          className="text-sm rounded-lg border border-slate-200 px-2 py-1.5" />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600">
          Avbryt
        </button>
        <button onClick={save} disabled={saving}
          data-testid="goal-form-save"
          className="text-xs px-4 py-1.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white font-semibold inline-flex items-center gap-1 disabled:opacity-50">
          {saving && <Loader2 className="w-3 h-3 animate-spin" />}
          {initial ? 'Uppdatera' : 'Skapa mål'}
        </button>
      </div>
    </div>
  );
}
